import { DatePicker, Modal, Select } from 'antd';
import dayjs from 'dayjs';
import type React from 'react';
import { useState } from 'react';
import { orderReportRangeLabelCompact, SHORT_DATETIME_FORMAT } from '../../utils/dateFormatUtils';

const { RangePicker } = DatePicker;

export type DateRange = [dayjs.Dayjs, dayjs.Dayjs];

const CUSTOM_KEY = 'custom';

// Ranges are functions, not values: evaluating them on selection keeps "Heute" meaning today
// even in a tab that has been open since yesterday.
const PRESETS: { key: string; label: string; range: () => DateRange }[] = [
  { key: 'today', label: 'Heute (tagesaktuell)', range: () => [dayjs().startOf('day'), dayjs().endOf('day')] },
  {
    key: 'yesterday',
    label: 'Gestern',
    range: () => [dayjs().subtract(1, 'day').startOf('day'), dayjs().subtract(1, 'day').endOf('day')],
  },
  { key: 'this-week', label: 'Diese Woche', range: () => [dayjs().startOf('week'), dayjs().endOf('day')] },
  {
    key: 'last-week',
    label: 'Letzte Woche',
    range: () => [dayjs().subtract(1, 'week').startOf('week'), dayjs().subtract(1, 'week').endOf('week')],
  },
  { key: 'this-month', label: 'Dieser Monat', range: () => [dayjs().startOf('month'), dayjs().endOf('day')] },
  {
    key: 'last-month',
    label: 'Letzter Monat',
    range: () => [dayjs().subtract(1, 'month').startOf('month'), dayjs().subtract(1, 'month').endOf('month')],
  },
];

// Minute precision, because that is all the picker offers — the seconds a preset carries
// (endOf('day') is 23:59:59.999) must not keep it from matching.
const matchingPresetKey = (value?: DateRange): string | undefined =>
  value &&
  PRESETS.find(preset => {
    const [from, to] = preset.range();
    return value[0].isSame(from, 'minute') && value[1].isSame(to, 'minute');
  })?.key;

const disabledDate = (current: dayjs.Dayjs) => current.isAfter(dayjs(), 'day');

interface Props {
  // injected by Form.Item
  id?: string;
  value?: DateRange;
  onChange?: (value: DateRange) => void;
}

export const DateRangeSelect: React.FC<Props> = ({ id, value, onChange }) => {
  const [draft, setDraft] = useState<DateRange | null>(null);

  const presetKey = matchingPresetKey(value);
  const customLabel = value ? orderReportRangeLabelCompact(...value) : '';

  const select = (key: string) => {
    if (key === CUSTOM_KEY) {
      setDraft(value ?? PRESETS[0].range());
      return;
    }
    onChange?.(PRESETS.find(preset => preset.key === key)?.range() ?? PRESETS[0].range());
  };

  const confirm = () => {
    if (draft) {
      onChange?.(draft);
    }
    setDraft(null);
  };

  return (
    <>
      <Select
        id={id}
        style={{ width: '100%' }}
        popupMatchSelectWidth={false}
        value={presetKey ?? (value ? CUSTOM_KEY : undefined)}
        onSelect={select}
        // The closed box shows the actual range, the dropdown row stays "Benutzerdefiniert…".
        labelRender={({ value: key, label }) => (key === CUSTOM_KEY ? customLabel : label)}
        options={[
          ...PRESETS.map(preset => ({ value: preset.key, label: preset.label })),
          { value: CUSTOM_KEY, label: 'Benutzerdefiniert…' },
        ]}
      />
      <Modal
        title="Zeitraum wählen"
        open={draft !== null}
        destroyOnHidden
        okText="Übernehmen"
        cancelText="Abbrechen"
        onOk={confirm}
        onCancel={() => setDraft(null)}
      >
        <RangePicker
          autoFocus
          allowEmpty={[false, false]}
          allowClear={false}
          showTime={{ format: 'HH:mm' }}
          format={SHORT_DATETIME_FORMAT}
          style={{ width: '100%' }}
          value={draft ?? undefined}
          onChange={dates => dates?.[0] && dates[1] && setDraft([dates[0], dates[1]])}
          disabledDate={disabledDate}
        />
      </Modal>
    </>
  );
};
